<script lang="ts" setup>
import { ref } from 'vue';
import { CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import IdentifierField from './IdentifierField.vue';
import { UserToCreate } from '../NewUser/useCreateMultipleUsers';
import { useLocale } from '../../composables/useLocale';

export type AlreadyExistsUser = { user: UserToCreate; type: 'exists' };
type Props = {
  user: UserToCreate;
  users: CustomerMgmtTypes.GetOrganizationUsersResponse['data'];
  disabled: boolean;
  hideTitle?: boolean;
};

const props = withDefaults(defineProps<Props>(), { hideTitle: false });

type Emits = {
  (e: 'update', key: string, userUid: CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number] | null): void;
};
const emit = defineEmits<Emits>();

const { t } = useLocale();

const selectedUserId = ref<string | null>(null);
const clickedToSelect = (existUser: CustomerMgmtTypes.GetOrganizationUsersResponse['data'][number]) => {
  if (selectedUserId.value === existUser.uid) {
    selectedUserId.value = null;
    emit('update', props.user.key, null);
  } else {
    selectedUserId.value = existUser.uid;
    emit('update', props.user.key, existUser);
  }
};

defineExpose({
  isValid: () => {
    return true;
  },
});
</script>

<template>
  <v-row>
    <v-col>
      <h4 v-if="!hideTitle">{{ t('lcp.user_already_exists', { name: user.name }) }}</h4>
      <p>
        {{ t('lcp.user_already_exists_explain') }}
      </p>
    </v-col>
  </v-row>
  <v-row class="mt-10">
    <v-col>
      <v-row>
        <v-col>
          <h4>{{ t('lcp.select_existing_user_or_create') }}</h4>
        </v-col>
      </v-row>
      <v-row class="my-0 px-2" v-for="u in users" :key="u.uid">
        <v-col>
          <v-row no-gutters>
            <v-col cols="auto" class="d-flex align-center">
              <v-row no-gutters class="ga-2 align-center">
                <v-col cols="auto">
                  <h4>{{ u.fullname }}</h4>
                </v-col>
                <v-col cols="auto">
                  <v-chip v-if="u.active && u.lastActiveAt">
                    {{ t('lcp.last_active') }}:
                    {{ new Date(u.lastActiveAt).toLocaleDateString().replace(/(\/)/g, '-') }}
                  </v-chip>
                  <v-chip v-if="!u.active">{{ t('lcp.inactive') }}</v-chip>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters class="ga-2">
            <IdentifierField v-if="u.email" :text="u.email" icon="mdi-email"></IdentifierField>
          </v-row>
        </v-col>
        <v-col cols="auto" class="d-flex align-center">
          <v-btn
            :variant="selectedUserId === u.uid ? undefined : 'tonal'"
            :prepend-icon="selectedUserId === u.uid ? 'mdi-check' : undefined"
            size="small"
            width="125"
            :disabled="disabled"
            @click="() => clickedToSelect(u)"
            >{{ t(selectedUserId !== u.uid ? 'lcp.select' : 'lcp.selected') }}</v-btn
          >
        </v-col>
        <v-divider class="mx-1" :thickness="2"></v-divider>
      </v-row>
    </v-col>
  </v-row>
</template>
